import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'store/app/entities/baseQuery';

const ommnioSignEndpoints = build => ({
  /**
   * Check OmmnioSign service health and availability
   */
  getStatus: build.query({
    query: () => ({
      url: 'v1/ommniosign/status',
      method: 'GET',
    }),
    providesTags: ['OmmnioSignStatus'],
  }),

  /**
   * Sign a PDF document with OmmnioSign
   * Backend already has the PDF - no upload needed
   */
  signDocument: build.mutation({
    query: ({ signerToken, evidenceId, widgets, consent }) => ({
      url: 'v1/ommniosign/sign',
      method: 'POST',
      body: {
        signerToken,
        evidenceId,
        widgets,
        consent,
      },
    }),
    invalidatesTags: ['OmmnioSignStatus'],
  }),

  /**
   * Request OTP code for signing
   * Backend gets employeeId from authenticated session and derives requestId from signerToken
   */
  requestOTP: build.mutation({
    query: ({ signerToken, appHash }) => ({
      url: 'v1/ommniosign/otp/send',
      method: 'POST',
      body: appHash ? { signerToken, appHash } : { signerToken },
    }),
  }),

  /**
   * Verify OTP code
   * Backend gets employeeId from authenticated session and derives requestId from signerToken
   */
  verifyOTP: build.mutation({
    query: ({ code, signerToken }) => ({
      url: 'v1/ommniosign/otp/verify',
      method: 'POST',
      body: { code, signerToken },
    }),
  }),

  /**
   * Authorize signing using an active authentication session (no OTP needed)
   * Backend derives requestId from signerToken and validates the active session
   */
  authorizeSession: build.mutation({
    query: ({ signerToken, signature, widgets }) => ({
      url: 'v1/ommniosign/session/authorize',
      method: 'POST',
      body: { signerToken, signature, widgets },
    }),
  }),

  /**
   * Read-only check for a reusable signing session (no SMS, no evidence).
   * Lets the signing UI skip the auth step without spending OTP/session capacity.
   */
  checkSessionStatus: build.mutation({
    query: ({ signerToken }) => ({
      url: 'v1/ommniosign/session/status',
      method: 'POST',
      body: { signerToken },
    }),
  }),

  /**
   * Get saved signatures for the authenticated user
   */
  getSavedSignatures: build.query({
    query: () => ({
      url: 'v1/ommniosign/signatures',
      method: 'GET',
    }),
    providesTags: ['SavedSignatures'],
  }),

  /**
   * Save a new signature
   */
  createSavedSignature: build.mutation({
    query: ({ type, data }) => ({
      url: 'v1/ommniosign/signatures',
      method: 'POST',
      body: { type, data },
    }),
    invalidatesTags: ['SavedSignatures'],
  }),

  /**
   * Delete a saved signature
   */
  deleteSavedSignature: build.mutation({
    query: ({ signatureId }) => ({
      url: `v1/ommniosign/signatures/${signatureId}`,
      method: 'DELETE',
    }),
    invalidatesTags: ['SavedSignatures'],
  }),
});

export const ommnioSignApi = createApi({
  reducerPath: 'ommnioSign',
  baseQuery,
  tagTypes: ['OmmnioSignStatus', 'SavedSignatures'],
  endpoints: ommnioSignEndpoints,
});

export const ommnioSignMiddleware = ommnioSignApi.middleware;
export const ommnioSignReducer = ommnioSignApi.reducer;

export const {
  useGetStatusQuery,
  useSignDocumentMutation,
  useRequestOTPMutation,
  useVerifyOTPMutation,
  useAuthorizeSessionMutation,
  useCheckSessionStatusMutation,
  useGetSavedSignaturesQuery,
  useCreateSavedSignatureMutation,
  useDeleteSavedSignatureMutation,
} = ommnioSignApi;
